import { useState } from 'react';

import type { Tag } from '@/api/tags';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';

import { TagPainel } from './TagPainel';

interface TagsLoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quantos registros estão selecionados. */
  quantidade: number;
  /** "processos", "clientes"… — para o texto. */
  rotulo: string;
  aplicando: boolean;
  onAplicar: (tagIds: string[], acao: 'adicionar' | 'remover') => Promise<void>;
}

/**
 * Aplicar ou remover tags em vários registros de uma vez (PLANO_TAGS D13): escolhe as
 * tags e então "Aplicar" ou "Remover". Mexe só nas tags escolhidas — as demais tags
 * de cada registro ficam como estão.
 */
export function TagsLoteDialog({ open, onOpenChange, quantidade, rotulo, aplicando, onAplicar }: TagsLoteDialogProps) {
  const [escolhidas, setEscolhidas] = useState<Set<string>>(new Set());

  const alternar = (tag: Tag) =>
    setEscolhidas((atual) => {
      const nova = new Set(atual);
      if (nova.has(tag.id)) nova.delete(tag.id);
      else nova.add(tag.id);
      return nova;
    });

  const executar = async (acao: 'adicionar' | 'remover') => {
    try {
      await onAplicar([...escolhidas], acao);
      setEscolhidas(new Set());
      onOpenChange(false);
    } catch {
      // O erro já foi mostrado; o diálogo fica aberto.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(valor) => {
        if (!valor) setEscolhidas(new Set());
        onOpenChange(valor);
      }}
    >
      <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-1rem)] max-w-lg flex-col gap-3 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>
            Tags em {quantidade} {rotulo}
          </DialogTitle>
          <DialogDescription>Escolha as tags e depois aplique ou remova de todos os selecionados.</DialogDescription>
        </DialogHeader>
        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          <TagPainel marcadas={escolhidas} onAlternar={alternar} onCriada={alternar} />
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            disabled={escolhidas.size === 0 || aplicando}
            onClick={() => executar('remover')}
            className="h-11 sm:h-9"
          >
            Remover
          </Button>
          <Button disabled={escolhidas.size === 0 || aplicando} onClick={() => executar('adicionar')} className="h-11 sm:h-9">
            {aplicando && <Spinner size={16} className="mr-2 text-inherit" />}
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
